import { useCallback, useEffect, useState } from 'react';
import {
  DeliveryQueueItem,
  DeliveryQueueJobStatusData,
  OverlappingJob,
  ProcessDeliveryQueueAsyncResult,
  ProcessDeliveryQueueSyncResult,
  deliveryQueueService,
} from '../services/firebaseFunctions';

const POLL_INTERVAL_MS = 3000;
const DEDUP_CONFLICT_CODE = 'functions/already-exists';

export function useDeliveryQueue() {
  const [items, setItems] = useState<DeliveryQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState('');
  const [productId, setProductId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<ProcessDeliveryQueueSyncResult | null>(null);
  const [asyncMeta, setAsyncMeta] = useState<Pick<
    ProcessDeliveryQueueAsyncResult,
    'totalItems' | 'partialResult' | 'message'
  > | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<DeliveryQueueJobStatusData | null>(null);
  const [overlappingJobs, setOverlappingJobs] = useState<OverlappingJob[]>([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await deliveryQueueService.list({
        companyId: companyId || undefined,
        productId: productId || undefined,
      });
      setItems(data);
    } catch {
      setError('Erro ao carregar entregas pendentes');
    } finally {
      setLoading(false);
    }
  }, [companyId, productId]);

  useEffect(() => {
    load();
  }, [load]);

  const processAll = useCallback(async () => {
    setProcessing(true);
    setProcessError(null);
    setSyncResult(null);
    setAsyncMeta(null);
    setJobStatus(null);
    setOverlappingJobs([]);

    try {
      const result = await deliveryQueueService.process({
        companyId: companyId || undefined,
        productId: productId || undefined,
      });

      setOverlappingJobs(result.overlappingJobs ?? []);

      if (result.mode === 'sync') {
        setSyncResult(result);
        await load();
      } else {
        setJobId(result.jobId);
        setAsyncMeta({
          totalItems: result.totalItems,
          partialResult: result.partialResult,
          message: result.message,
        });
        setJobStatus({
          jobId: result.jobId,
          status: 'processing',
          totalItems: result.totalItems,
          processedItems: result.partialResult.resolved
            + result.partialResult.stillPending
            + result.partialResult.errors,
          resolved: result.partialResult.resolved,
          stillPending: result.partialResult.stillPending,
          errors: result.partialResult.errors,
          createdAt: new Date().toISOString(),
        });
      }
    } catch (caught) {
      const code = (caught as { code?: string })?.code;
      const isDedupConflict = code === DEDUP_CONFLICT_CODE;
      setProcessError(
        isDedupConflict && caught instanceof Error
          ? caught.message
          : 'Erro ao processar entregas pendentes',
      );
    } finally {
      setProcessing(false);
    }
  }, [companyId, productId, load]);

  useEffect(() => {
    if (!jobId) return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        const status = await deliveryQueueService.getJobStatus(jobId);
        if (cancelled) return;
        setJobStatus(status);
        if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
          setJobId(null);
          await load();
        }
      } catch {
        if (!cancelled) {
          setProcessError('Erro ao acompanhar progresso do processamento');
          setJobId(null);
        }
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [jobId, load]);

  return {
    items,
    loading,
    error,
    companyId,
    setCompanyId,
    productId,
    setProductId,
    pendingCount: items.length,
    processAll,
    processing,
    processError,
    syncResult,
    asyncMeta,
    jobStatus,
    overlappingJobs,
    reload: load,
  };
}
